import React, { useState, useEffect } from 'react'
import { useParams, Link } from 'react-router-dom'

export default function PartView() {
  const { id } = useParams()
  const [part, setPart] = useState(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    fetch(`/api/parts/${id}`)
      .then(response => response.json())
      .then(data => {
        setPart(data)
        setLoading(false)
      })
      .catch(err => {
        console.error('Error fetching part:', err)
        setLoading(false)
      })
  }, [id])

  const formatCurrency = (amount) => {
    if (!amount && amount !== 0) return '-'
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount)
  }

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="text-gray-500">Loading...</div>
      </div>
    )
  }

  if (!part) {
    return (
      <div className="px-4 sm:px-0">
        <div className="text-center py-12">
          <p className="text-gray-500">Part not found</p>
          <Link to="/parts" className="mt-4 text-gray-600 hover:text-gray-900">
            &larr; Back to Parts
          </Link>
        </div>
      </div>
    )
  }

  return (
    <div className="px-4 sm:px-0">
      <div className="mb-6 flex justify-between items-center">
        <Link to="/parts" className="text-gray-600 hover:text-gray-900">
          &larr; Back to Parts
        </Link>
        <Link
          to={`/parts/${id}/edit`}
          className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-gray-900 hover:bg-gray-800"
        >
          Edit Part
        </Link>
      </div>

      <div className="bg-white shadow rounded-lg overflow-hidden">
        <div className="p-6">
          <div className="flex items-start space-x-6">
            {/* Photo */}
            {part.photo_url ? (
              <img
                src={part.photo_url}
                alt={part.description}
                className="h-48 w-48 object-cover rounded-lg border"
              />
            ) : (
              <div className="h-48 w-48 bg-gray-200 rounded-lg flex items-center justify-center">
                <span className="text-gray-400">No photo</span>
              </div>
            )}

            {/* Details */}
            <div className="flex-1">
              <h1 className="text-2xl font-bold text-gray-900 mb-4">
                {part.description}
              </h1>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-gray-500">Price</p>
                  <p className="text-lg font-semibold">{formatCurrency(part.price)}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Stock Value</p>
                  <p className="text-lg font-semibold">{formatCurrency(part.stock_value)}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Quantity in Stock</p>
                  <p className="text-lg font-semibold">{part.quantity_in_stock}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Order When Below</p>
                  <p className="text-lg font-semibold">{part.reorder_threshold}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Status</p>
                  <span
                    className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                      part.needs_reorder
                        ? 'bg-red-100 text-red-800'
                        : 'bg-green-100 text-green-800'
                    }`}
                  >
                    {part.needs_reorder ? 'Needs Reorder' : 'In Stock'}
                  </span>
                </div>
                {part.needs_reorder && (
                  <div>
                    <p className="text-sm text-gray-500">Quantity to Order</p>
                    <p className="text-lg font-semibold">{part.quantity_to_order}</p>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Vendor Information */}
          {(part.vendor_name || part.vendor_website) && (
            <div className="mt-6 pt-6 border-t">
              <h2 className="text-lg font-medium text-gray-900 mb-4">Vendor Information</h2>
              <div className="grid grid-cols-2 gap-4">
                {part.vendor_name && (
                  <div>
                    <p className="text-sm text-gray-500">Vendor Name</p>
                    <p className="text-base">{part.vendor_name}</p>
                  </div>
                )}
                {part.vendor_website && (
                  <div>
                    <p className="text-sm text-gray-500">Vendor Website</p>
                    <a
                      href={part.vendor_website}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-blue-600 hover:text-blue-800 hover:underline"
                    >
                      {part.vendor_website}
                    </a>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Notes */}
          <div className="mt-6 pt-6 border-t">
            <h2 className="text-lg font-medium text-gray-900 mb-4">Notes</h2>
            {part.notes ? (
              <p className="text-base text-gray-700 whitespace-pre-wrap">{part.notes}</p>
            ) : (
              <p className="text-base text-gray-400 italic">No notes for this part yet.</p>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}
